/* ==========================================================================
   MARV — theme behaviour
   No brand values in here; all copy comes from window.MarvTheme.strings,
   which layout/theme.liquid fills from the locale files.
   ========================================================================== */
(function () {
  'use strict';

  var Theme = window.MarvTheme || {};
  var routes = Theme.routes || {};
  var strings = Theme.strings || {};

  var prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  /* ---------------------------------------------------------------- utils */

  function on(el, evt, fn, opts) {
    if (el) el.addEventListener(evt, fn, opts);
  }

  function $(sel, scope) {
    return (scope || document).querySelector(sel);
  }

  function $$(sel, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(sel));
  }

  function formatMoney(cents) {
    // Prefer the server-rendered strings wherever possible. This is only a
    // fallback for optimistic UI between a click and the section re-render.
    var format = Theme.moneyFormat || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, parts.join('.'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, parts[0])
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, parts.join(','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, parts[0]);
  }

  var toastTimer;
  function toast(message) {
    var el = document.getElementById('MarvToast');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    // force reflow so the transition runs
    void el.offsetWidth;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      el.classList.remove('is-visible');
      setTimeout(function () {
        el.hidden = true;
      }, 220);
    }, 2600);
  }

  function trapFocus(container, opener) {
    if (!container) return function () {};
    var selector =
      'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function handle(event) {
      if (event.key !== 'Tab') return;
      var focusable = $$(selector, container).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', handle);
    var target = container.querySelector(selector);
    if (target) target.focus();

    return function release() {
      container.removeEventListener('keydown', handle);
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }

  /* --------------------------------------------------------- scroll reveal */

  var revealObserver = null;

  function initReveal(scope) {
    if (Theme.animateOnScroll === false || prefersReducedMotion) {
      $$('[data-reveal]', scope).forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      $$('[data-reveal]', scope).forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var el = entry.target;
            var step =
              parseInt(
                getComputedStyle(document.documentElement).getPropertyValue(
                  '--reveal-stagger'
                ),
                10
              ) || 90;
            var delay = (Number(el.dataset.stagger) || 0) * step;
            el.style.transition =
              'opacity .62s ease-out ' +
              delay +
              'ms, transform .62s ease-out ' +
              delay +
              'ms';
            el.classList.add('is-revealed');
            revealObserver.unobserve(el);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );
    }

    $$('[data-reveal]', scope).forEach(function (el) {
      if (!el.classList.contains('is-revealed')) revealObserver.observe(el);
    });
  }

  /* -------------------------------------------------------- sticky header */

  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;

    // The sticky element is the section wrapper, not the header itself.
    var headerSection = header.closest('.header-section') || header;

    if (header.classList.contains('header--transparent')) {
      // The sentinel has to sit outside the sticky wrapper, otherwise it
      // travels with it and never reports leaving the viewport. Zero height
      // keeps it out of the flow entirely — a 1px box here painted the
      // body's white background as a hairline above the transparent header.
      var sentinel = document.createElement('div');
      sentinel.style.cssText = 'height:0;width:100%;pointer-events:none;';
      headerSection.parentNode.insertBefore(sentinel, headerSection);

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          function (entries) {
            header.classList.toggle('is-stuck', !entries[0].isIntersecting);
          },
          { threshold: 0 }
        ).observe(sentinel);
      }
    }

    // Publish chrome heights so the header and any sticky panel below it can
    // offset themselves instead of stacking on top of each other.
    function publishHeight() {
      var bar = $('[data-announcement]');
      var root = document.documentElement;

      // Only a *sticky* announcement bar pushes the header down; one that
      // scrolls away contributes nothing.
      var barHeight = 0;
      if (bar && bar.offsetParent !== null && bar.classList.contains('announcement--sticky')) {
        barHeight = bar.offsetHeight;
      }

      root.style.setProperty('--announcement-height', barHeight + 'px');
      // The bar's own height whether it sticks or not: what a full-screen
      // section has to give up to end exactly at the bottom of the screen.
      root.style.setProperty('--announcement-space', (bar && bar.offsetParent !== null ? bar.offsetHeight : 0) + 'px');
      root.style.setProperty('--header-height', header.offsetHeight + 'px');
      root.style.setProperty('--chrome-height', barHeight + header.offsetHeight + 'px');
    }

    publishHeight();
    on(window, 'resize', publishHeight);

    // The bar can be dismissed after load, which changes every offset.
    var annBar = $('[data-announcement]');
    if (annBar && 'MutationObserver' in window) {
      new MutationObserver(publishHeight).observe(annBar.parentNode, { childList: true });
    }
  }

  /* --------------------------------------------------- announcement close */

  function initAnnouncement() {
    var bar = $('[data-announcement]');
    if (!bar) return;
    /* The key includes a fingerprint of the message text, so changing the
       messages produces a new key and the bar returns for everyone. */
    var key = 'marv:announcement:' + (bar.dataset.announcementKey || 'default');
    var remindDays = parseInt(bar.dataset.remindDays, 10);
    if (isNaN(remindDays)) remindDays = 7;

    /* A timestamp rather than a flag, so a dismissal expires. Stored forever,
       one dismissal would hide every future announcement permanently. */
    try {
      var dismissedAt = parseInt(localStorage.getItem(key), 10);
      if (!isNaN(dismissedAt)) {
        if (remindDays > 0 && Date.now() - dismissedAt < remindDays * 864e5) {
          bar.remove();
          return;
        }
        // Expired — drop the record so storage does not accumulate.
        localStorage.removeItem(key);
      }
    } catch (e) {
      /* storage blocked — just show the bar */
    }

    on($('[data-announcement-close]', bar), 'click', function () {
      bar.remove();
      try {
        localStorage.setItem(key, String(Date.now()));
      } catch (e) {}
      // Sticky offsets change the moment it leaves.
      window.dispatchEvent(new Event('resize'));
    });

    setupMarquee(bar);
  }

  /* Seamless marquee.

     The track ships with two copies of the message set. That only loops
     cleanly when one set is already wider than the bar — otherwise the strip
     runs out mid-track and snaps back, which reads as a stutter halfway
     through. So clone the set until the track is at least twice the bar's
     width, then shift by exactly one set's width instead of a blunt -50%.

     Shifting by one set width also keeps the speed constant: adding clones
     changes the track length but not the distance travelled per cycle. */
  function setupMarquee(bar) {
    var track = $('.announcement__track', bar);
    var viewport = $('.announcement__viewport', bar);
    if (!track || !viewport) return;
    // A static (non-scrolling) bar has no marquee to size.
    if (bar.classList.contains('announcement--static')) return;
    if (prefersReducedMotion) {
      track.style.animation = 'none';
      return;
    }

    var groups = $$('.announcement__group', track);
    if (!groups.length) return;

    var unit = groups[0];

    function measure() {
      // Width of one message set, including the gap that follows it.
      var width = unit.getBoundingClientRect().width;
      if (!width) return;

      var needed = viewport.getBoundingClientRect().width * 2;
      var current = $$('.announcement__group', track).length * width;

      // Guard against a runaway loop if width is reported oddly.
      var safety = 0;
      while (current < needed && safety < 20) {
        var clone = unit.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
        current += width;
        safety++;
      }

      track.style.setProperty('--marquee-shift', '-' + width + 'px');
    }

    measure();

    // Re-measure once webfonts land, since they change the text width.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure).catch(function () {});
    }

    var resizeTimer;
    on(window, 'resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measure, 150);
    });
  }

  /* ------------------------------------------------------ policy pages */

  var WORDMARK = /(^|[^\w@./-])(marv)(?![\w@/])(?!(?:-[\w-]+)?\.[a-z]{2,})/gi;

  /* The policy editor in the admin has no list button. Every list in the
     policies is therefore typed as a run of one-line paragraphs: a lead-in
     ending in a colon, the items, then the prose picks up again. Nothing in
     the markup says "list", so a reader gets a wall of short paragraphs with
     no way to see where one point ends and the next begins.

     Rebuilt here into a real <ul>. A run qualifies only when at least two
     paragraphs in a row are short and unpunctuated — a finished sentence, or
     anything long, ends it. That is what keeps the prose resuming after a
     list from being swallowed into it. */
  function groupPolicyLists(scope) {
    var host = $('.rte', scope) || scope;
    var nodes = Array.prototype.slice.call(host.children);
    var i = 0;

    while (i < nodes.length) {
      var lead = nodes[i];

      if (lead.tagName !== 'P' || !/:$/.test(lead.textContent.trim())) {
        i++;
        continue;
      }

      var items = [];
      var j = i + 1;

      while (j < nodes.length) {
        var el = nodes[j];
        if (el.tagName !== 'P') break;
        var text = el.textContent.trim();
        if (!text || text.length > 90 || /[.!?]$/.test(text)) break;
        items.push(el);
        j++;
      }

      if (items.length >= 2) {
        var list = document.createElement('ul');
        items.forEach(function (para) {
          var item = document.createElement('li');
          while (para.firstChild) item.appendChild(para.firstChild);
          list.appendChild(item);
          para.parentNode.removeChild(para);
        });
        lead.parentNode.insertBefore(list, lead.nextSibling);
      }

      i = j;
    }
  }

  /* The contact block runs the brand straight into the address —
     "<strong>MARV</strong>Lahore, Pakistan" — because the line break after
     the name was never typed, and the policy editor gives no way to see it.

     Only a paragraph that opens with a bolded name followed immediately by a
     letter qualifies: no space, no punctuation, nothing. Run before the
     emphasis pass, so the only bold in the document is still the author's.  */
  function spacePolicyContact(scope) {
    var host = $('.rte', scope) || scope;

    $$('p', host).forEach(function (para) {
      var lead = para.firstChild;
      if (!lead || lead.nodeType !== 1 || lead.tagName !== 'STRONG') return;

      /* A break typed at the end of the bolded name counts. The editor puts
         it inside the <strong>, where it is invisible in the source but does
         the job — adding another would open a blank line. */
      var tail = lead.lastChild;
      if (tail && tail.nodeType === 1 && tail.tagName === 'BR') return;

      var after = lead.nextSibling;
      if (!after || after.nodeType !== 3) return;
      if (!/^[A-Za-z]/.test(after.nodeValue)) return;

      para.insertBefore(document.createElement('br'), after);
    });
  }

  var EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

  /* The policies print the support address as plain text, so a reader who
     wants to use it has to select and copy it by hand — on a phone, from the
     middle of a paragraph. Linked here instead.

     Run before the wordmark and emphasis passes, which both skip anchors:
     once the address is a link, marv-store.com inside it is protected from
     every later pass without needing a rule of its own. */
  function linkPolicyEmails(scope) {
    if (!document.createTreeWalker) return;

    var walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, null, false);
    var targets = [];
    var node;

    while ((node = walker.nextNode())) {
      if (node.parentElement && node.parentElement.closest('a')) continue;
      EMAIL.lastIndex = 0;
      if (EMAIL.test(node.nodeValue)) targets.push(node);
    }

    targets.forEach(function (text) {
      var value = text.nodeValue;
      var frag = document.createDocumentFragment();
      var last = 0;
      var match;

      EMAIL.lastIndex = 0;

      while ((match = EMAIL.exec(value))) {
        if (match.index > last) {
          frag.appendChild(document.createTextNode(value.slice(last, match.index)));
        }
        var link = document.createElement('a');
        link.href = 'mailto:' + match[0];
        link.textContent = match[0];
        frag.appendChild(link);
        last = match.index + match[0].length;
      }

      if (last < value.length) frag.appendChild(document.createTextNode(value.slice(last)));
      text.parentNode.replaceChild(frag, text);
    });
  }

  /* The policies spell the brand three ways — Marv, marv and MARV — having
     been written at different times. Normalised to the wordmark.

     The pattern refuses a match touching a word character, @, dot, slash or
     hyphen on either side, so marv-store.com and marvclothing.com are left
     alone: a domain is not the wordmark. Links are skipped for the same
     reason. */
  function normaliseWordmark(scope) {
    if (!document.createTreeWalker) return;

    var walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, null, false);
    var pending = [];
    var node;

    while ((node = walker.nextNode())) {
      if (node.parentElement && node.parentElement.closest('a')) continue;

      var next = node.nodeValue.replace(WORDMARK, function (whole, before) {
        return before + 'MARV';
      });

      if (next !== node.nodeValue) pending.push([node, next]);
    }

    /* Applied after the walk; rewriting nodes mid-traversal invalidates it. */
    pending.forEach(function (pair) {
      pair[0].nodeValue = pair[1];
    });
  }

  var EMPHASIS = /(?:Rs\.?\s?\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?)|(?:\d+(?:\s*[–—-]\s*\d+)?\s+(?:working\s+|business\s+)?(?:days?|hours?|weeks?|months?))|\b(?:not responsible|not liable|non-returnable|non-refundable|not eligible|not (?:a )?guaranteed|do not offer|do not accept|no international shipping)\b/gi;

  /* The two things a reader actually looks for in a policy, and the two that
     get argued about afterwards: the figures — how long, how much, by when —
     and the hard limits, the sentences saying what is not covered. Neither
     can be emphasised in the admin's policy editor, which offers no
     formatting at all, so it is done here.

     Emphasis only. Nothing is reworded, reordered or hidden; the sentence a
     customer reads is the sentence that was written. */
  function emphasisePolicyTerms(scope) {
    if (!document.createTreeWalker) return;

    var walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, null, false);
    var targets = [];
    var node;

    while ((node = walker.nextNode())) {
      var parent = node.parentElement;
      if (!parent) continue;
      /* Headings carry their own weight, the index is navigation, and a term
         already emphasised or inside a link is left as the author had it. */
      if (parent.closest('a, strong, b, h1, h2, h3, h4, .policy-toc')) continue;

      EMPHASIS.lastIndex = 0;
      if (EMPHASIS.test(node.nodeValue)) targets.push(node);
    }

    targets.forEach(function (text) {
      var value = text.nodeValue;
      var frag = document.createDocumentFragment();
      var last = 0;
      var match;

      EMPHASIS.lastIndex = 0;

      while ((match = EMPHASIS.exec(value))) {
        if (!match[0].length) {
          EMPHASIS.lastIndex++;
          continue;
        }
        if (match.index > last) {
          frag.appendChild(document.createTextNode(value.slice(last, match.index)));
        }
        var strong = document.createElement('strong');
        strong.textContent = match[0];
        frag.appendChild(strong);
        last = match.index + match[0].length;
      }

      if (last < value.length) frag.appendChild(document.createTextNode(value.slice(last)));
      text.parentNode.replaceChild(frag, text);
    });
  }

  /* Shopify renders /policies/* with its own markup and ignores a theme's
     policy template, so the "On this page" index from the design has to be
     built here from whatever headings the policy body contains. */
  function initPolicyPage() {
    var container = $('.shopify-policy__container');
    var body = $('.shopify-policy__body');
    if (!container || !body) return;

    /* Shopify writes the policy h1 from its own fixed name for the page. */
    if (Theme.policyHeading) {
      var policyTitle = $('.shopify-policy__title h1', container);
      if (policyTitle) policyTitle.textContent = Theme.policyHeading;
    }

    /* All of these run ahead of the index, so a policy too short to earn one
       is still repaired. Order matters twice: the contact block is spaced
       while the only bold in the document is still the author's, and the
       address becomes a link before the passes that skip links run. */
    groupPolicyLists(body);
    spacePolicyContact(body);
    linkPolicyEmails(body);
    normaliseWordmark(container);
    emphasisePolicyTerms(body);

    var headings = $$('h2, h3', body);
    if (headings.length < 3) return; // too short to be worth an index

    var aside = document.createElement('aside');
    aside.className = 'policy-aside';

    var nav = document.createElement('nav');
    nav.setAttribute('aria-label', 'On this page');

    var label = document.createElement('span');
    label.className = 'eyebrow';
    label.textContent = 'On this page';
    nav.appendChild(label);

    var list = document.createElement('div');
    list.className = 'policy-toc';
    nav.appendChild(list);
    aside.appendChild(nav);

    var pairs = [];

    headings.forEach(function (heading, i) {
      if (!heading.id) {
        var slug = (heading.textContent || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 48);
        heading.id = slug ? 'policy-' + slug : 'policy-section-' + i;
      }

      var a = document.createElement('a');
      a.href = '#' + heading.id;
      a.textContent = (heading.textContent || '').trim();
      list.appendChild(a);
      pairs.push({ link: a, heading: heading });
    });

    // Insert the index before the body so it lands in the left grid column.
    container.insertBefore(aside, body);
    container.classList.add('has-policy-toc');

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            pairs.forEach(function (pair) {
              pair.link.classList.toggle('is-active', pair.heading === entry.target);
            });
          });
        },
        { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
      );
      headings.forEach(function (h) {
        observer.observe(h);
      });
    }
  }

  /* ------------------------------------------------------- rich text */

  /* Tables pasted into a page or product description carry inline widths from
     the editor and will overflow a narrow container. Wrapping each one in a
     scroller keeps it readable on a phone without stretching the layout. */
  function initRteTables(scope) {
    $$('.rte table', scope).forEach(function (table) {
      if (table.parentNode && table.parentNode.classList.contains('table-scroll')) {
        return;
      }
      var wrap = document.createElement('div');
      wrap.className = 'table-scroll';
      wrap.setAttribute('tabindex', '0');
      wrap.setAttribute('role', 'region');
      var caption = table.querySelector('caption');
      wrap.setAttribute(
        'aria-label',
        caption ? caption.textContent.trim() : 'Table'
      );
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    });
  }

  /* ------------------------------------------------------ discount popup */

  function initPopup() {
    var popup = $('[data-newsletter-popup]');
    if (!popup) return;

    var key = 'marv:popup:' + (popup.dataset.key || 'default');
    var release = null;

    /* Two different questions, which were one flag and should never have
       been. isOpen is whether it is on screen this moment. autoSpent is
       whether it has already had its one uninvited appearance — which a
       dismissal also spends, because being waved away is an answer. */
    var isOpen = false;
    var autoSpent = false;
    var delayTimer = null;

    // Rendered server-side when the customer form posted successfully.
    var isSuccess = !!$('[data-popup-success]', popup);

    function store(k, v) {
      try {
        localStorage.setItem(k, v);
      } catch (e) {
        /* private mode — the popup simply reappears next visit */
      }
    }

    function read(k) {
      try {
        return localStorage.getItem(k);
      } catch (e) {
        return null;
      }
    }

    /* byRequest is true only when a visitor asked for it — the message in the
       announcement bar. Everything else is the popup showing up on its own,
       and it gets one turn per visit. */
    function open(byRequest) {
      if (isOpen) return;
      if (!byRequest && autoSpent) return;

      if (!byRequest) autoSpent = true;
      isOpen = true;
      popup.hidden = false;
      document.body.classList.add('marv-no-scroll');
      release = trapFocus($('[data-popup-panel]', popup));
      /* Recorded on open, not only on close. Otherwise someone who ignores
         the popup and reloads is shown it again on every single page view. */
      store(key + ':seen', String(Date.now()));
    }

    function close() {
      popup.hidden = true;
      document.body.classList.remove('marv-no-scroll');
      if (release) release();
      release = null;

      /* No longer on screen, and done asking. Clearing only isOpen is what
         let a dismissed popup come straight back the moment the reader
         scrolled past the threshold: "No thanks" was being treated as
         closing a window rather than as declining the offer. The pending
         delay is dropped too, so a popup refused inside the first few
         seconds does not reappear when the timer runs out.

         The announcement message still opens it, because that is a request. */
      isOpen = false;
      autoSpent = true;
      if (delayTimer) {
        clearTimeout(delayTimer);
        delayTimer = null;
      }
      store(key + ':seen', String(Date.now()));
    }

    $$('[data-popup-close]', popup).forEach(function (btn) {
      on(btn, 'click', close);
    });

    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && !popup.hidden) close();
    });

    // Copy the revealed code
    var codeBtn = $('[data-popup-copy]', popup);
    if (codeBtn) {
      on(codeBtn, 'click', function () {
        var code = codeBtn.dataset.popupCopy;
        var original = codeBtn.textContent;
        var done = function () {
          codeBtn.textContent = codeBtn.dataset.copiedLabel || original;
          setTimeout(function () {
            codeBtn.textContent = original;
          }, 1800);
        };
        if (navigator.clipboard) {
          navigator.clipboard.writeText(code).then(done).catch(function () {});
        }
      });
    }

    /* Delegated, because the marquee clones its message groups after this
       runs — a direct listener would miss every clone.

       Registered here, ahead of every check below. Those decide whether the
       popup opens by itself; somebody who clicks the message is asking for
       it, and has to be answered whether or not they have already seen it,
       dismissed it, or subscribed. Wired up after them, the announcement
       message went dead for a month the first time the popup appeared. */
    on(document, 'click', function (e) {
      var trigger = e.target.closest('[data-open-discount-popup]');
      if (!trigger) return;
      e.preventDefault();
      open(true);
    });

    /* Just subscribed: show the code straight away and never ask again. */
    if (isSuccess) {
      store(key + ':subscribed', '1');
      open(true);

      /* Shopify signals success with a query parameter, and Liquid rebuilds
         the success state from it on every render. Left in place, reloading
         or navigating back re-opens the code panel indefinitely. Clearing it
         makes the reveal a one-time event without losing the current page. */
      if (window.history && window.history.replaceState) {
        try {
          var url = new URL(window.location.href);
          url.searchParams.delete('customer_posted');
          url.hash = '';
          window.history.replaceState({}, '', url.toString());
        } catch (e) {
          /* older browser — the panel simply reappears on reload */
        }
      }
      return;
    }

    if (popup.hasAttribute('data-force-open')) {
      open(true);
      return;
    }

    if (read(key + ':subscribed')) return;

    var remindDays = parseInt(popup.dataset.remindDays, 10) || 30;
    var seen = parseInt(read(key + ':seen'), 10);
    if (seen && Date.now() - seen < remindDays * 864e5) return;

    /* Whichever trigger fires first. A delay alone catches someone reading;
       the scroll threshold catches someone who is already browsing. */
    var delay = parseInt(popup.dataset.delay, 10);
    if (!isNaN(delay)) {
      delayTimer = setTimeout(function () {
        open(false);
      }, delay * 1000);
    }

    var scrollPct = parseInt(popup.dataset.scroll, 10);
    if (scrollPct > 0) {
      var onScroll = function () {
        var max = document.documentElement.scrollHeight - window.innerHeight;
        if (max <= 0) return;
        if ((window.scrollY / max) * 100 >= scrollPct) {
          open(false);
          window.removeEventListener('scroll', onScroll);
          return;
        }
        /* Already had its turn — stop listening rather than keep asking. */
        if (autoSpent) window.removeEventListener('scroll', onScroll);
      };
      on(window, 'scroll', onScroll, { passive: true });
    }

  }

  /* --------------------------------------------------------- live search */

  /* The search page made you press Enter and reload to see anything. Results
     now follow the typing.

     Shopify's Section Rendering API returns this very section rendered for a
     different query, so the results that arrive are the same markup the
     server would have sent on a reload — same cards, same filtering, same
     count — rather than a second, thinner rendering kept in step by hand.

     The address bar is kept in step with replaceState, so the page can be
     shared or reloaded and shows what was on screen, and so Back leaves the
     search rather than walking through every letter typed. */
  function initLiveSearch() {
    var form = $('[data-search-form]');
    var input = form && $('[data-search-input]', form);
    var section = $('[data-search-section]');
    if (!form || !input || !section) return;

    var results = $('[data-search-results]', section);
    if (!results) return;

    var sectionId = section.dataset.searchSection;
    var timer = null;
    var inFlight = null;
    var lastQuery = input.value.trim();

    function render(query) {
      var url =
        (routes.search || '/search') +
        '?q=' +
        encodeURIComponent(query) +
        '&options%5Bprefix%5D=last&section_id=' +
        encodeURIComponent(sectionId);

      if (inFlight) inFlight.abort();
      inFlight = typeof AbortController === 'function' ? new AbortController() : null;

      results.setAttribute('aria-busy', 'true');

      fetch(url, inFlight ? { signal: inFlight.signal } : undefined)
        .then(function (res) {
          if (!res.ok) throw new Error('search failed');
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-search-results]');
          if (!fresh) return;

          results.innerHTML = fresh.innerHTML;
          results.setAttribute('aria-busy', 'false');

          /* Reveal and rails are bound per element, so the cards that just
             arrived need wiring the same as any section the editor reloads. */
          initAll(results);

          if (window.history && window.history.replaceState) {
            var next = query
              ? (routes.search || '/search') + '?q=' + encodeURIComponent(query)
              : routes.search || '/search';
            window.history.replaceState({}, '', next);
          }
        })
        .catch(function (err) {
          results.setAttribute('aria-busy', 'false');
          if (err && err.name === 'AbortError') return;
          /* Leave whatever is on screen; Enter still submits the form. */
        });
    }

    on(input, 'input', function () {
      var query = input.value.trim();
      if (query === lastQuery) return;
      lastQuery = query;

      if (timer) clearTimeout(timer);
      /* Long enough that a word is not fetched letter by letter, short
         enough that it still feels like it is keeping up. */
      timer = setTimeout(function () {
        render(query);
      }, 280);
    });

    /* Enter would reload the page to the state already on screen. */
    on(form, 'submit', function (event) {
      event.preventDefault();
      if (timer) clearTimeout(timer);
      render(input.value.trim());
    });
  }

  /* ------------------------------------------------------------- sharing */

  /* Was an inline onclick that called navigator.share, fell back to the
     clipboard, and said nothing either way — so on a desktop the button
     looked broken, because copying a link is invisible.

     Delegated, so it works on a product section the editor has just
     reloaded. A cancelled share sheet is a decision, not a failure, and is
     left alone. */
  function initShare() {
    on(document, 'click', function (event) {
      var btn = event.target.closest('[data-share-product]');
      if (!btn) return;

      event.preventDefault();

      var payload = { title: document.title, url: window.location.href };

      if (navigator.share) {
        navigator.share(payload).catch(function () {
          /* Dismissed the sheet. Nothing to report. */
        });
        return;
      }

      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(payload.url)
          .then(function () {
            toast(strings.linkCopied || 'Link copied');
          })
          .catch(function () {
            toast(strings.linkCopyFailed || 'Could not copy the link');
          });
      }
    });
  }

  /* ------------------------------------------------- account preconnect */

  /* Signing in means a hop to account.<domain>, which is a different origin
     on a different address, so the browser opens a fresh connection to it
     part-way through the redirect. Nothing in the theme can shorten Shopify's
     hosted sign-in, but that handshake can be over before the click.

     Opened on the first sign of intent rather than on load: pointing at the
     link, tabbing to it, or touching it. Anyone who never goes near it pays
     nothing. */
  function initAccountPreconnect() {
    var host = window.location.hostname;
    if (host.indexOf('myshopify.com') !== -1) return;

    var links = $('a[href$="/account"], a[href*="/account/login"], a[href*="/account/register"]');
    if (!links.length) return;

    var opened = false;

    function warm() {
      if (opened) return;
      opened = true;

      var link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = 'https://account.' + host;
      link.crossOrigin = '';
      document.head.appendChild(link);
    }

    links.forEach(function (anchor) {
      on(anchor, 'pointerenter', warm);
      on(anchor, 'focus', warm);
      on(anchor, 'touchstart', warm, { passive: true });
    });
  }

  /* -------------------------------------------------------- back to top */

  function initBackToTop() {
    var btn = $('[data-back-to-top]');
    if (!btn) return;

    btn.hidden = false;

    function toggle() {
      // Appear once the visitor is a screen or so down the page.
      var past = window.scrollY > window.innerHeight * 0.75;
      btn.classList.toggle('is-visible', past);
    }

    on(btn, 'click', function () {
      window.scrollTo({
        top: 0,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
      // Move focus back to the top of the document for keyboard users.
      var main = document.getElementById('MainContent');
      if (main) main.focus({ preventScroll: true });
    });

    on(window, 'scroll', toggle, { passive: true });
    toggle();
  }

  /* ---------------------------------------------------------- mobile nav */

  function initMobileNav() {
    var nav = $('[data-mobile-nav]');
    if (!nav) return;
    var release = null;

    /* The drawer is markup inside the header section, and that section's
       wrapper is sticky at z-index 50 — under the announcement bar's wrapper
       at 60. A z-index only ranks against siblings inside its own stacking
       context, so the drawer's 95 counts for nothing out here and the bar
       paints straight across the top of it, cutting off the logo.

       The section itself has to out-rank the bar, and only while the drawer
       is open, so the bar still sits above the header the rest of the time. */
    var section = nav.closest('.shopify-section');

    function open(opener) {
      nav.classList.add('is-open');
      if (section) section.classList.add('has-drawer-open');
      document.body.classList.add('marv-no-scroll');
      release = trapFocus($('[data-mobile-nav-panel]', nav), opener);
    }

    function close() {
      if (!nav.classList.contains('is-open')) return;

      var panel = $('[data-mobile-nav-panel]', nav);

      /* The parts that should go at once: the page is scrollable again and
         focus returns to whatever opened the drawer, both the moment the
         reader asks to close. Only the drawer itself waits. */
      document.body.classList.remove('marv-no-scroll');
      if (release) release();
      release = null;

      var settled = false;

      function finish() {
        if (settled) return;
        settled = true;
        if (panel) panel.removeEventListener('animationend', finish);
        nav.classList.remove('is-open', 'is-closing');
        if (section) section.classList.remove('has-drawer-open');
      }

      if (!panel || prefersReducedMotion.matches) {
        finish();
        return;
      }

      nav.classList.add('is-closing');
      panel.addEventListener('animationend', finish);
      /* A safety net: if the animation never reports back — a background tab,
         a browser that drops the event — the drawer still goes. */
      setTimeout(finish, 450);
    }

    $$('[data-mobile-nav-open]').forEach(function (btn) {
      on(btn, 'click', function () {
        open(btn);
      });
    });

    $$('[data-mobile-nav-close]', nav).forEach(function (btn) {
      on(btn, 'click', close);
    });

    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) close();
    });
  }

  /* -------------------------------------------------------------- modals */

  function initModals() {
    $$('[data-modal]').forEach(function (modal) {
      var id = modal.dataset.modal;
      var release = null;

      function open(opener) {
        modal.classList.add('is-open');
        document.body.classList.add('marv-no-scroll');
        release = trapFocus($('[data-modal-panel]', modal), opener);
      }

      function close() {
        modal.classList.remove('is-open');
        document.body.classList.remove('marv-no-scroll');
        if (release) release();
        release = null;
      }

      $$('[data-modal-open="' + id + '"]').forEach(function (btn) {
        on(btn, 'click', function () {
          open(btn);
        });
      });

      $$('[data-modal-close]', modal).forEach(function (btn) {
        on(btn, 'click', close);
      });

      on(document, 'keydown', function (e) {
        if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
      });
    });
  }

  /* ---------------------------------------------------------------- rails */

  function initRails(scope) {
    $$('[data-rail]', scope).forEach(function (wrapper) {
      if (wrapper.dataset.railReady === 'true') return;
      wrapper.dataset.railReady = 'true';

      var track = $('[data-rail-track]', wrapper);
      var prev = $('[data-rail-prev]', wrapper);
      var next = $('[data-rail-next]', wrapper);
      var bar = $('[data-rail-progress]', wrapper);
      if (!track) return;

      /* One card plus the real gap, so an arrow click lands exactly on the
         next snap point rather than drifting a few pixels each time. */
      function step() {
        var first = track.firstElementChild;
        if (!first) return track.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      }

      function update() {
        var max = track.scrollWidth - track.clientWidth;
        var pct = max > 0 ? (track.scrollLeft / max) * 100 : 100;
        if (bar) bar.style.width = pct + '%';
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
      }

      on(prev, 'click', function () {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      on(next, 'click', function () {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });
      on(track, 'scroll', update, { passive: true });
      on(window, 'resize', update);

      /* A horizontal rail with a hidden scrollbar has no affordance on
         desktop: a vertical mouse wheel does not move it, and there is no
         bar to drag. Map a vertical wheel onto horizontal scroll while the
         pointer is over the rail, but hand the gesture back to the page at
         either end so the rail never traps the reader. */
      on(
        track,
        'wheel',
        function (event) {
          if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

          var max = track.scrollWidth - track.clientWidth;
          if (max <= 1) return;

          var atStart = track.scrollLeft <= 0 && event.deltaY < 0;
          var atEnd = track.scrollLeft >= max - 1 && event.deltaY > 0;
          if (atStart || atEnd) return;

          event.preventDefault();
          track.scrollLeft += event.deltaY;
        },
        { passive: false }
      );

      /* Click and drag, the way a carousel is expected to behave. Touch is
         left alone since native touch scrolling already works. */
      var dragging = false;
      var startX = 0;
      var startLeft = 0;
      var moved = false;

      on(track, 'pointerdown', function (event) {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        dragging = true;
        moved = false;
        startX = event.clientX;
        startLeft = track.scrollLeft;
        track.classList.add('is-dragging');
      });

      on(track, 'pointermove', function (event) {
        if (!dragging) return;
        var dx = event.clientX - startX;
        if (Math.abs(dx) > 3) moved = true;
        track.scrollLeft = startLeft - dx;
      });

      function endDrag() {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-dragging');
      }

      on(track, 'pointerup', endDrag);
      on(track, 'pointercancel', endDrag);
      on(track, 'pointerleave', endDrag);

      /* Touch, with the axis decided once per gesture and then held.

         touch-action: pan-y hands vertical scrolling to the browser
         untouched, so the page scrolls over a rail exactly as it does
         anywhere else. Horizontal is ours. The first few pixels of movement
         choose the axis, and that choice stands until the finger lifts —
         which is the whole point. Left on auto the browser kept re-reading
         the gesture as it went, so a swipe that began sideways and drifted
         was handed back to the page half-way through and the view juddered
         between the two.

         CSS snapping is switched off for the duration, because it pulls
         against a position being set by hand, and applied again on release
         by scrolling to the nearest card. */
      var AXIS_THRESHOLD = 8;
      var axis = null;
      var touchX = 0;
      var touchY = 0;
      var touchLeft = 0;
      var lastX = 0;
      var lastStamp = 0;
      var speed = 0;
      var glideFrame = null;

      function stopGlide() {
        if (!glideFrame) return;
        cancelAnimationFrame(glideFrame);
        glideFrame = null;
      }

      function settle() {
        track.classList.remove('is-dragging');
        var width = step();
        if (!width) return;
        var target = Math.round(track.scrollLeft / width) * width;
        track.scrollTo({ left: target, behavior: 'smooth' });
      }

      /* Carries the flick on after the finger leaves, the way the browser's
         own scrolling does, then settles on a card. */
      function glide() {
        var perFrame = speed * 16;

        if (prefersReducedMotion.matches || Math.abs(perFrame) < 0.4) {
          settle();
          return;
        }

        var limit = track.scrollWidth - track.clientWidth;

        var tick = function () {
          perFrame *= 0.94;
          track.scrollLeft -= perFrame;

          if (
            Math.abs(perFrame) < 0.4 ||
            track.scrollLeft <= 0 ||
            track.scrollLeft >= limit
          ) {
            glideFrame = null;
            settle();
            return;
          }

          glideFrame = requestAnimationFrame(tick);
        };

        glideFrame = requestAnimationFrame(tick);
      }

      on(
        track,
        'touchstart',
        function (event) {
          stopGlide();

          /* Two fingers is a pinch, not a pan. Leave it entirely. */
          if (event.touches.length !== 1) {
            axis = 'y';
            return;
          }

          axis = null;
          speed = 0;
          touchX = lastX = event.touches[0].clientX;
          touchY = event.touches[0].clientY;
          touchLeft = track.scrollLeft;
          lastStamp = Date.now();
        },
        { passive: true }
      );

      on(
        track,
        'touchmove',
        function (event) {
          if (axis === 'y' || event.touches.length !== 1) return;

          var touch = event.touches[0];
          var dx = touch.clientX - touchX;
          var dy = touch.clientY - touchY;

          if (axis === null) {
            if (Math.abs(dx) < AXIS_THRESHOLD && Math.abs(dy) < AXIS_THRESHOLD) {
              return;
            }
            axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
            if (axis === 'y') return;
            track.classList.add('is-dragging');
          }

          /* Ours now: stop the browser doing anything else with it. */
          event.preventDefault();
          track.scrollLeft = touchLeft - dx;

          var now = Date.now();
          var elapsed = now - lastStamp;
          if (elapsed > 0) speed = (touch.clientX - lastX) / elapsed;
          lastX = touch.clientX;
          lastStamp = now;
        },
        { passive: false }
      );

      function endTouch() {
        if (axis === 'x') glide();
        axis = null;
      }

      on(track, 'touchend', endTouch);
      on(track, 'touchcancel', endTouch);


      // A drag that ends on a link must not also follow it.
      on(
        track,
        'click',
        function (event) {
          if (moved) {
            event.preventDefault();
            event.stopPropagation();
            moved = false;
          }
        },
        true
      );

      update();

      // Images and webfonts change the track width, so re-measure once they
      // settle or the arrows can start out wrongly disabled.
      on(window, 'load', update);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(update).catch(function () {});
      }
    });
  }

  /* ----------------------------------------------------- quantity inputs */

  function initQuantity(scope) {
    $$('[data-qty]', scope).forEach(function (widget) {
      if (widget.dataset.qtyReady === 'true') return;
      widget.dataset.qtyReady = 'true';

      var input = $('input', widget);
      if (!input) return;

      function clamp(value) {
        var min = parseInt(input.min, 10) || 0;
        var max = input.max ? parseInt(input.max, 10) : Infinity;
        return Math.min(Math.max(value, min), max);
      }

      $$('[data-qty-change]', widget).forEach(function (btn) {
        on(btn, 'click', function () {
          var delta = parseInt(btn.dataset.qtyChange, 10) || 0;
          var next = clamp((parseInt(input.value, 10) || 0) + delta);
          if (next === parseInt(input.value, 10)) return;
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ----------------------------------------------------------------- cart */

  var Cart = {
    busy: false,

    /* The Section Rendering API matches on the real section id. A static
       section rendered with {% section 'x' %} is simply "x", but a section
       inside a group or template is "sections--123__x" / "template--123__main"
       — so the cart page reports its own id through a data attribute rather
       than us guessing it. Asking for an id that does not exist fails
       silently and nothing updates. */
    sectionsToRender: function () {
      var ids = [];
      if ($('[data-cart-drawer]')) ids.push('cart-drawer');

      var cartPage = $('[data-cart-page]');
      if (cartPage && cartPage.dataset.sectionId) {
        ids.push(cartPage.dataset.sectionId);
      }

      return ids;
    },

    request: function (url, body) {
      var sections = this.sectionsToRender();
      if (sections.length) body.sections = sections.join(',');
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      }).then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            var err = new Error(data.description || data.message || strings.cartError);
            err.data = data;
            throw err;
          }
          return data;
        });
      });
    },

    add: function (formDataOrPayload) {
      return this.request(routes.cartAdd, formDataOrPayload);
    },

    change: function (payload) {
      return this.request(routes.cartChange, payload);
    },

    renderSections: function (data) {
      if (!data || !data.sections) return;

      Object.keys(data.sections).forEach(function (id) {
        var html = data.sections[id];
        if (!html) return;

        var parsed = new DOMParser().parseFromString(html, 'text/html');

        if (id === 'cart-drawer') {
          var source = parsed.querySelector('[data-cart-drawer-contents]');
          var target = $('[data-cart-drawer-contents]');
          if (source && target) target.innerHTML = source.innerHTML;
        } else {
          var cartSource = parsed.querySelector('[data-cart-page]');
          var cartTarget = $('[data-cart-page]');
          if (cartSource && cartTarget) cartTarget.innerHTML = cartSource.innerHTML;
        }
      });

      Cart.updateCount();
      initQuantity(document);
      Drawer.bind();
    },

    /* The header sits in a section group, so re-rendering it just for a
       number is wasteful. The freshly rendered cart markup already carries
       the count, and /cart.js is the fallback when the drawer is switched
       off entirely. */
    updateCount: function () {
      var badge = $('[data-drawer-count]');

      if (badge) {
        var count = badge.textContent.trim();
        $$('[data-cart-count]').forEach(function (el) {
          el.textContent = count;
        });
        return Promise.resolve();
      }

      return fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          $$('[data-cart-count]').forEach(function (el) {
            el.textContent = cart.item_count;
          });
        })
        .catch(function () {});
    }
  };

  /* --------------------------------------------------------- cart drawer */

  var Drawer = {
    release: null,

    el: function () {
      return $('[data-cart-drawer]');
    },

    open: function (opener) {
      var drawer = this.el();
      if (!drawer) return;
      drawer.classList.add('is-open');
      document.body.classList.add('marv-no-scroll');
      this.release = trapFocus($('[data-drawer-panel]', drawer), opener);
    },

    close: function () {
      var drawer = this.el();
      if (!drawer) return;
      drawer.classList.remove('is-open');
      document.body.classList.remove('marv-no-scroll');
      if (this.release) this.release();
      this.release = null;
    },

    bind: function () {
      var drawer = this.el();
      if (!drawer) return;

      $$('[data-drawer-close]', drawer).forEach(function (btn) {
        if (btn.dataset.bound === 'true') return;
        btn.dataset.bound = 'true';
        on(btn, 'click', function () {
          Drawer.close();
        });
      });

      // Line item quantity + remove
      $$('[data-line-change]', drawer).forEach(function (el) {
        if (el.dataset.bound === 'true') return;
        el.dataset.bound = 'true';

        var evt = el.tagName === 'INPUT' ? 'change' : 'click';
        on(el, evt, function (event) {
          event.preventDefault();
          if (Cart.busy) return;

          var line = parseInt(el.dataset.line, 10);
          var quantity =
            el.tagName === 'INPUT'
              ? parseInt(el.value, 10)
              : parseInt(el.dataset.lineChange, 10);

          if (isNaN(line) || isNaN(quantity)) return;

          Cart.busy = true;
          drawer.setAttribute('aria-busy', 'true');

          Cart.change({ line: line, quantity: Math.max(0, quantity) })
            .then(function (data) {
              Cart.renderSections(data);
            })
            .catch(function (err) {
              toast(err.message || strings.cartError);
            })
            .then(function () {
              Cart.busy = false;
              drawer.removeAttribute('aria-busy');
            });
        });
      });
    },

    init: function () {
      var self = this;

      $$('[data-cart-open]').forEach(function (btn) {
        on(btn, 'click', function (event) {
          if (Theme.cartType !== 'drawer' || !self.el()) return;
          event.preventDefault();
          self.open(btn);
        });
      });

      on(document, 'keydown', function (e) {
        var drawer = self.el();
        if (e.key === 'Escape' && drawer && drawer.classList.contains('is-open')) {
          self.close();
        }
      });

      this.bind();
    }
  };

  /* ------------------------------------------------------------ quick add */

  function initQuickAdd() {
    on(document, 'click', function (event) {
      var btn = event.target.closest('[data-quick-add]');
      if (!btn) return;

      event.preventDefault();
      var variantId = btn.dataset.quickAdd;
      if (!variantId || Cart.busy) return;

      Cart.busy = true;
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;

      Cart.add({ items: [{ id: Number(variantId), quantity: 1 }] })
        .then(function (data) {
          Cart.renderSections(data);
          if (Theme.cartType === 'drawer' && $('[data-cart-drawer]')) {
            Drawer.open(btn);
          } else {
            toast(strings.addedToCart);
          }
        })
        .catch(function (err) {
          toast(err.message || strings.cartError);
        })
        .then(function () {
          Cart.busy = false;
          btn.removeAttribute('aria-busy');
          btn.disabled = false;
        });
    });
  }

  /* -------------------------------------------------------- product forms */

  function initProductForms(scope) {
    $$('[data-product-form]', scope).forEach(function (form) {
      if (form.dataset.bound === 'true') return;
      form.dataset.bound = 'true';

      on(form, 'submit', function (event) {
        event.preventDefault();
        if (Cart.busy) return;

        var submit = $('[type="submit"]', form);
        var body = {};
        new FormData(form).forEach(function (value, key) {
          body[key] = value;
        });

        Cart.busy = true;
        if (submit) {
          submit.setAttribute('aria-busy', 'true');
          submit.disabled = true;
        }

        Cart.add(body)
          .then(function (data) {
            Cart.renderSections(data);
            if (Theme.cartType === 'drawer' && $('[data-cart-drawer]')) {
              Drawer.open(submit);
            } else {
              toast(strings.addedToCart);
            }
          })
          .catch(function (err) {
            toast(err.message || strings.cartError);
          })
          .then(function () {
            Cart.busy = false;
            if (submit) {
              submit.removeAttribute('aria-busy');
              submit.disabled = false;
            }
          });
      });
    });
  }

  /* ------------------------------------------------- variant selection */

  function initVariantPickers(scope) {
    $$('[data-variant-picker]', scope).forEach(function (picker) {
      if (picker.dataset.bound === 'true') return;
      picker.dataset.bound = 'true';

      var sectionId = picker.dataset.section;
      var productUrl = picker.dataset.url;
      var dataEl = $('[data-variant-json]', picker);
      if (!dataEl) return;

      var variants;
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      function selectedOptions() {
        var values = [];
        $$('[data-option-index]', picker).forEach(function (input) {
          var idx = parseInt(input.dataset.optionIndex, 10);
          if (input.type === 'radio') {
            if (input.checked) values[idx] = input.value;
          } else {
            values[idx] = input.value;
          }
        });
        return values;
      }

      function matchVariant(values) {
        return variants.find(function (variant) {
          return variant.options.every(function (opt, i) {
            return opt === values[i];
          });
        });
      }

      function update() {
        var values = selectedOptions();
        var variant = matchVariant(values);

        // Reflect the chosen value in the option label
        $$('[data-option-value-for]', picker).forEach(function (label) {
          var idx = parseInt(label.dataset.optionValueFor, 10);
          if (values[idx]) label.textContent = values[idx];
        });

        var idInput = $('[data-variant-id]', picker.closest('[data-product-root]') || document);
        var submit = $('[data-add-button]', picker.closest('[data-product-root]') || document);
        var submitText = submit ? $('[data-add-button-text]', submit) : null;

        if (!variant) {
          if (submit) submit.disabled = true;
          if (submitText) submitText.textContent = strings.unavailable;
          return;
        }

        if (idInput) idInput.value = variant.id;

        if (submit) {
          submit.disabled = !variant.available;
          if (submitText) {
            submitText.textContent = variant.available
              ? strings.addToCart
              : strings.soldOut;
          }
        }

        // Update the URL so refresh/share keeps the variant
        if (productUrl && window.history.replaceState) {
          window.history.replaceState(
            {},
            '',
            productUrl + '?variant=' + variant.id
          );
        }

        // Ask the server for fresh price/media markup for this variant
        if (sectionId) {
          fetch(productUrl + '?variant=' + variant.id + '&section_id=' + sectionId)
            .then(function (r) {
              return r.text();
            })
            .then(function (html) {
              var parsed = new DOMParser().parseFromString(html, 'text/html');
              ['[data-price-block]', '[data-inventory-block]', '[data-sku-block]'].forEach(
                function (sel) {
                  var source = parsed.querySelector(sel);
                  var target = $(sel);
                  if (source && target) target.innerHTML = source.innerHTML;
                }
              );
            })
            .catch(function () {
              /* keep the optimistic UI */
            });
        }

        picker.dispatchEvent(
          new CustomEvent('variant:change', { bubbles: true, detail: { variant: variant } })
        );
      }

      on(picker, 'change', update);
      update();
    });
  }

  /* ------------------------------------------------------ product gallery */

  function initGalleries(scope) {
    $$('[data-gallery]', scope).forEach(function (gallery) {
      if (gallery.dataset.bound === 'true') return;
      gallery.dataset.bound = 'true';

      var main = $('[data-gallery-main]', gallery);

      $$('[data-gallery-thumb]', gallery).forEach(function (thumb) {
        on(thumb, 'click', function () {
          var mediaId = thumb.dataset.galleryThumb;
          $$('[data-gallery-slide]', gallery).forEach(function (slide) {
            slide.hidden = slide.dataset.gallerySlide !== mediaId;
          });
          $$('[data-gallery-thumb]', gallery).forEach(function (t) {
            t.setAttribute('aria-current', String(t === thumb));
          });
          if (main) main.scrollIntoView({ block: 'nearest' });
        });
      });

      // Keep the gallery in sync with variant changes
      on(gallery.closest('[data-product-root]') || document, 'variant:change', function (
        event
      ) {
        var variant = event.detail && event.detail.variant;
        if (!variant || !variant.featured_media) return;
        var thumb = $(
          '[data-gallery-thumb="' + variant.featured_media.id + '"]',
          gallery
        );
        if (thumb) thumb.click();
      });
    });
  }

  /* ------------------------------------------------- collection filtering */

  function initFacets(scope) {
    $$('[data-facet-form]', scope).forEach(function (form) {
      if (form.dataset.bound === 'true') return;
      form.dataset.bound = 'true';

      function apply() {
        var params = new URLSearchParams(new FormData(form));
        // Drop empty values so the URL stays clean
        Array.from(params.keys()).forEach(function (key) {
          if (!params.get(key)) params.delete(key);
        });
        var url = form.dataset.facetForm + (params.toString() ? '?' + params : '');
        window.location.href = url;
      }

      on(form, 'change', apply);
    });
  }

  /* ------------------------------------------------------------- sections */

  function initAll(scope) {
    initReveal(scope);
    initRails(scope);
    initQuantity(scope);
    initProductForms(scope);
    initVariantPickers(scope);
    initGalleries(scope);
    initFacets(scope);
    initRteTables(scope);
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    initHeader();
    initAnnouncement();
    initBackToTop();
    initPopup();
    initPolicyPage();
    initAccountPreconnect();
    initShare();
    initLiveSearch();
    initMobileNav();
    initModals();
    initQuickAdd();
    Drawer.init();
    initAll(document);
  });

  // Theme editor hooks — re-init whatever the editor swaps in.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
    initModals();
    Drawer.init();
  });
  document.addEventListener('shopify:section:select', function (event) {
    initReveal(event.target);
  });

  window.MarvTheme = Object.assign(Theme, {
    cart: Cart,
    drawer: Drawer,
    toast: toast,
    formatMoney: formatMoney,
    refresh: initAll
  });
})();
